import { useEffect } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import "@src/App.css";
import { useMutation } from "@tanstack/react-query";
import { Flex, Box, Separator, Text, Container } from "@radix-ui/themes";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { recordSession, getSurveySections } from "@api/survey";
import { Trans } from "@lingui/macro";
import { usePersistentSurveyStore, useSurveyStore } from "@src/store/survey";
import Map from "@components/Map";
import SurveySectionControls from "@components/SurveySectionControls";
import SurveyQuestions from "@components/SurveyQuestions";
import Header from "@components/Header";
import Loading from "@components/Loading";
import Error from "@components/Error";
import LanguageSelector from "@components/LanguageSelector";
import InfoDialog from "@components/InfoDialog";
import SurveySectionNumber from "@components/SurveySectionNumber";

function Survey() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const userUUID = usePersistentSurveyStore().getUserUUID();
  const language = usePersistentSurveyStore((state) => state.language);
  const sessionUUID = useSurveyStore((state) => state.sessionUUID);
  const setCurrentSurveySection = useSurveyStore(
    (state) => state.setCurrentSurveySection,
  );
  const surveySections = useSurveyStore((state) => state.surveySections);
  const setSurveySections = useSurveyStore((state) => state.setSurveySections);
  const params = useParams();

  const { isPending, isError, data, error, isFetching } = useQuery({
    queryKey: ["surveySections", getSurveySections, language],
    queryFn: () => getSurveySections(),
    placeholderData: keepPreviousData,
    staleTime: 180000, // Three minutes
  });

  const session = useMutation({ mutationFn: recordSession });

  useEffect(() => {
    if (!data) return;
    setSurveySections(data);
  }, [data, setSurveySections]);

  useEffect(() => {
    let slug = params.surveySectionSlug;
    setCurrentSurveySection(slug);

    if (slug === undefined) {
      slug = surveySections?.surveys?.[0]?.slug;
    }

    if (!slug) return;

    if (
      searchParams.has("latitude") &&
      searchParams.has("longitude") &&
      searchParams.has("zoom")
    ) {
      navigate(
        `/section/${slug}?latitude=${searchParams.get("latitude")}&longitude=${searchParams.get("longitude")}&zoom=${searchParams.get("zoom")}`,
      );
    } else {
      // Redirect to first survey section
      navigate(`/section/${slug}`);
    }
  }, [
    surveySections,
    params.surveySectionSlug,
    searchParams,
    navigate,
    setCurrentSurveySection,
  ]);

  // ideally this should be moved to a subscription on the store
  useEffect(() => {
    if (!sessionUUID || !params.surveySectionSlug || !userUUID) return;
    session.mutate({
      sessionUUID: sessionUUID,
      userUUID: userUUID,
      surveySectionSlug: params.surveySectionSlug,
    });
  }, [sessionUUID, params.surveySectionSlug, userUUID]);

  if (isError)
    return (
      <Container size="1" align="center" p="3">
        <Error>
          <Text color="red" size="1">
            <Trans id="Survey.load_error" variables={{ error: error.message }}>
              Could not load survey. {error.message}
            </Trans>
          </Text>
        </Error>
      </Container>
    );

  if (isPending || isFetching)
    return (
      <Box size="1" p="3">
        <Loading>
          <Text size="2">
            <Trans id="Survey.loading">Loading</Trans>
          </Text>
        </Loading>
      </Box>
    );

  return (
    <Flex id="bleh">
      <Box id="questions">
        <Box p="3">
          <Header name={surveySections?.name} />
          <Flex justify="between" align="center" gap="3" direction="row" pt="1">
            <LanguageSelector />
            <Flex
              justify="between"
              align="center"
              gap="3"
              direction="row"
              pt="1"
            >
              {surveySections?.description && <InfoDialog />}
              <SurveySectionNumber />
            </Flex>
          </Flex>
        </Box>
        <Separator my="3" size="4" />
        <Box pb="3" pl="4" pr="4">
          <SurveyQuestions />
        </Box>
      </Box>
      <Map />
      <Box ml="4" mr="4" mb="5" id="mobile-controls">
        <SurveySectionControls isFetching={isFetching || isPending || !data} />
      </Box>
    </Flex>
  );
}

export default Survey;
